import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Check, ArrowRight, Info } from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import {
  getCoBranchNames, GetcompanyNames, fetchChequeReturnCharges,
  submitChequeReturnCharges, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, isEmptyResult, inlineNoticeColor } from '../utils/noData';

const STATUS_OPTIONS = [
  { label: 'Allowed(0/-)', value: 'true' },
  { label: 'NotAllowed(250/-)', value: 'false' },
];

function ChequeReturnChargesPermissionForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [caoList, setCaoList] = useState([]);
  const [cao, setCao] = useState('');
  // Resolved from the CAO rather than picked: one CAO maps to one company, so
  // it is shown read-only instead of being a second thing to choose.
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [newAllowed, setNewAllowed] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOldStatus, setPendingOldStatus] = useState('');
  const [pendingNewStatus, setPendingNewStatus] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  // Allowing the charge waives it, so an allowed company pays nothing and a
  // not-allowed one pays the standard 250. Both the current-status box and the
  // dropdown spell the amount out, so nobody has to remember which way round it
  // goes.
  const statusLabel = (allowed) => (allowed ? 'Allowed(0/-)' : 'NotAllowed(250/-)');

  // Read the current status off the charge itself (the backend derives its
  // "allowed" flag the same way):
  // a company carrying the 250 charge is NotAllowed, one carrying nothing is
  // Allowed. Any non-zero amount counts as charged, so an amount other than 250
  // still reads as NotAllowed rather than falling through to Allowed(0/-).
  const currentlyAllowed = config ? !(Number(config.amount) > 0) : false;
  const currentStatusLabel = config ? statusLabel(currentlyAllowed) : '';
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const isValid = !!config && newAllowed !== '' && reason.trim().length >= 10 && reason.length <= 200;

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!connectionId) return;
    (async () => {
      try {
        const res = await getCoBranchNames();
        setCaoList(Array.isArray(res) ? res : []);
        // Inline rather than a toast: this runs on mount, before the user has
        // touched anything, and a popup on arrival reads as an error.
        if (isEmptyResult(res)) setLoadError(NO_DATA_MESSAGE);
      } catch (e) {
        setLoadError('Could not load CAO branches from the client API');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  const handleCao = async (value) => {
    setCao(value || '');
    setCompanyName('');
    setCompanyCode('');
    setNewAllowed('');
    setConfig(null);
    setLoadError('');
    if (!value) return;

    // The CAO gives the company, and the company gives the charges — the second
    // call cannot start until the first has produced a code.
    setLoadingCompany(true);
    let code = '';
    try {
      const res = await GetcompanyNames(value);
      const first = Array.isArray(res) ? res[0] : null;
      if (!first) {
        setLoadError('No company is configured for that CAO.');
        return;
      }
      setCompanyName(first.company_name || '');
      code = first.company_code || '';
      setCompanyCode(code);
    } catch (e) {
      setLoadError('Could not load the company for that CAO');
      return;
    } finally {
      setLoadingCompany(false);
    }

    if (!code) return;
    setLoadingConfig(true);
    try {
      setConfig(await fetchChequeReturnCharges(connectionId, code));
    } catch (e) {
      setLoadError('Could not load cheque return charges for that company');
    } finally {
      setLoadingConfig(false);
    }
  };

  /** Step 1: validate, stage the pending change, open the confirm modal. */
  const confirmSave = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingOldStatus(currentStatusLabel);
    setPendingNewStatus(statusLabel(newAllowed === 'true'));
    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after the user clicks "Yes, Save" in the modal. */
  const doSave = async () => {
    setSaving(true);
    try {
      const res = await submitChequeReturnCharges(connectionId, {
        configId: config.configId, currentAmount: config.amount, allowed: newAllowed === 'true',
      });
      const changed = res.changed;
      try {
        await saveData([{
          schemaName: null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'CHEQUE_RETURN_CHARGES_PERMISSION',
          oldData: {
            companyCode, configId: config.configId, amount: config.amount, allowed: currentlyAllowed,
          },
          newData: {
            companyCode, configId: config.configId, amount: config.amount, allowed: newAllowed === 'true', changed,
          },
          reason: reason.trim(),
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast(
          'success',
          'Success',
          `${pendingOldStatus} → ${pendingNewStatus}${!changed ? ' (already in that state — no change needed)' : ''}.`,
          true,
        );
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Permission updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (e) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Save Failed', e?.response?.data?.detail || 'Could not save this change. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    setCompanyName('');
    setCompanyCode('');
    setConfig(null);
    setLoadError('');
    setNewAllowed('');
    setReason('');
    setTouched(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="cheque-return-charges-toast" />

      <div key={formKey} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="cheque-return-charges-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
        <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>Cheque Return Charges Permission</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select CAO</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose a CAO to load its company and cheque return charges permission</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '760px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>CAO *</label>
            <SearchableSelect
              options={caoList}
              value={cao}
              onChange={handleCao}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_code}
              placeholder="Search CAO..."
              testId="cheque-return-charges-cao"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Company Name</label>
            <input
              className="input-field"
              style={{ fontSize: '13px', background: '#F9FAFB' }}
              value={loadingCompany ? 'Loading...' : companyName}
              readOnly
              placeholder="—"
              data-testid="cheque-return-charges-company"
            />
          </div>
        </div>
        {loadError && <div style={{ fontSize: '11px', color: inlineNoticeColor(loadError), marginTop: '4px' }}>{loadError}</div>}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!config} hint="Select a CAO in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Current Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the selected company</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Cheque Return Charges (₹)</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={config?.amount ?? ''} readOnly data-testid="cheque-return-charges-amount" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Current Permission Status</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={currentStatusLabel} readOnly data-testid="cheque-return-charges-current-status" />
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!config} hint="Select a CAO in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Update Permission</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the new cheque return charges permission status</div>
          </div>
        </div>

        <div style={{ maxWidth: '280px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>New Permission Status *</label>
          <SearchableSelect
            options={STATUS_OPTIONS}
            value={newAllowed}
            onChange={setNewAllowed}
            getLabel={(o) => o.label}
            getValue={(o) => o.value}
            placeholder={'Search status...'}
            disabled={!config}
            testId="cheque-return-charges-new-status"
          />
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!config || newAllowed === ''} hint="Choose the new permission status in section C first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Explain why this permission is being changed..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="cheque-return-charges-reason"
        />
        <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
          {reason.length} / 200
        </div>
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
        </SectionLock>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="cheque-return-charges-clear-button">Clear</button>
          <button type="button" onClick={confirmSave} className="btn-primary" disabled={saving} data-testid="cheque-return-charges-save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="cheque-return-charges-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle size={18} color="#002FA7" weight="bold" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Permission Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the cheque return charges permission for this company. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>From</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldStatus}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>To</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewStatus}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="cheque-return-charges-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="cheque-return-charges-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChequeReturnChargesPermissionForm;